import { Fragment, useEffect, useState } from 'react';
import { api, getToken, qs } from '../api/client';
import { urlImpression } from '../api/config';
import { Bandeau, Carte, Champ, Squelette, Vide } from '../components/ui';
import { dateHeureFr, roleLabel } from '../lib/format';

interface Ligne {
  _id: string; date: string; nomUtilisateur: string; role: string; action: string;
  entite: string; entiteId?: string; libelle: string;
  avant?: Record<string, unknown>; apres?: Record<string, unknown>; motif?: string; appareil?: string;
}
interface Reponse { lignes: Ligne[]; total: number; page: number; pages: number }

const COULEUR: Record<string, string> = {
  creer: 'var(--succes)', modifier: 'var(--attention)', annuler: 'var(--danger)',
  deroger: 'var(--violet)', cloturer: 'var(--primaire)', change: 'var(--violet)',
  restituer_caution: 'var(--primaire)', resilier: 'var(--danger)',
};

/**
 * Les entites exactement telles que le serveur les ecrit dans le journal.
 * Un libelle qui differe d'un seul accent rend le filtre muet : il cherche
 * « Réservation » alors que la base contient « Reservation ».
 * La correspondance est verifiee par scripts/verifier-langue.mjs.
 */
const ENTITES_JOURNAL = [
  'Amende',
  'CaisseSession',
  'Caution',
  'Client',
  'Contrat',
  'Devise',
  'Entretien',
  'EtatDesLieux',
  'Facture',
  'MouvementCaisse',
  'Paramètres',
  'Reservation',
  'Sinistre',
  'Supplément',
  'Système',
  'TauxChange',
  'Utilisateur',
  'Véhicule',
];

export default function Journal() {
  const [d, setD] = useState<Reponse | null>(null);
  const [page, setPage] = useState(1);
  const [entite, setEntite] = useState('');
  const [ouvert, setOuvert] = useState<string | null>(null);

  useEffect(() => { api.get<Reponse>(`/journal${qs({ page, entite })}`).then(setD); }, [page, entite]);
  if (!d) return <Squelette lignes={8} />;

  return (
    <>
      <div className="entete">
        <div className="titres"><h1>Journal d'activité</h1><p>{d.total} entrées · qui a fait quoi, et ce qui a changé</p></div>
        <div className="actions">
          <a className="btn" href={urlImpression('/api/journal/export', getToken())} onClick={(e) => {
            e.preventDefault();
            void api.get<string>('/journal/export').then((csv) => {
              const b = new Blob([csv], { type: 'text/csv;charset=utf-8' });
              const a = document.createElement('a');
              a.href = URL.createObjectURL(b); a.download = 'journal-friends.csv'; a.click();
            });
          }}>Exporter en CSV</a>
        </div>
      </div>

      <div style={{ marginBottom: 14 }}>
        <Bandeau type="info">
          Le journal est inaltérable : aucune entree ne peut être modifiée ni supprimée, par personne.
        </Bandeau>
      </div>

      <Carte pad={false} action={
        <select value={entite} onChange={(e) => { setEntite(e.target.value); setPage(1); }}
                style={{ height: 32, borderRadius: 8, border: '1px solid var(--bord-fort)', padding: '0 8px' }}>
          <option value="">Toutes les entites</option>
          {ENTITES_JOURNAL.map((x) => <option key={x}>{x}</option>)}
        </select>} titre="Entrées">
        {d.lignes.length === 0 ? <div className="carte-p"><Vide titre="Aucune entrée" /></div> : (
          <div className="enveloppe-tbl">
            <table className="tbl compact">
              <thead><tr><th>Horodatage</th><th>Utilisateur</th><th>Action</th><th>Entité</th><th>Ce qui s'est passé</th><th /></tr></thead>
              <tbody>
                {d.lignes.map((l) => (
                  <Fragment key={l._id}>
                    <tr onClick={() => setOuvert(ouvert === l._id ? null : l._id)} style={{ cursor: 'pointer' }}>
                      <td className="num mini">{dateHeureFr(l.date)}</td>
                      <td><b>{l.nomUtilisateur}</b><div className="mini sourdine">{roleLabel(l.role)}</div></td>
                      <td><span style={{ color: COULEUR[l.action] ?? 'var(--texte2)', fontWeight: 600 }}>{l.action.replace(/_/g, ' ')}</span></td>
                      <td className="mini sourdine">{l.entite}</td>
                      <td>{l.libelle}{l.motif && <div className="mini sourdine">motif : {l.motif}</div>}</td>
                      <td className="droite mini sourdine">{(l.avant || l.apres) ? (ouvert === l._id ? '▾' : '▸') : ''}</td>
                    </tr>
                    {ouvert === l._id && (l.avant || l.apres) && (
                      <tr>
                        <td colSpan={6} style={{ background: 'var(--zebre)', padding: 12 }}>
                          <div className="grille g2">
                            <div>
                              <div className="micro">Avant</div>
                              <pre style={{ fontSize: 12, margin: 0, whiteSpace: 'pre-wrap' }}>{JSON.stringify(l.avant ?? {}, null, 2)}</pre>
                            </div>
                            <div>
                              <div className="micro">Après</div>
                              <pre style={{ fontSize: 12, margin: 0, whiteSpace: 'pre-wrap' }}>{JSON.stringify(l.apres ?? {}, null, 2)}</pre>
                            </div>
                          </div>
                          {l.appareil && <div className="mini sourdine" style={{ marginTop: 8 }}>Appareil : {l.appareil}</div>}
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Carte>

      {d.pages > 1 && (
        <div className="actions" style={{ marginTop: 14, justifyContent: 'center' }}>
          <button className="btn btn-s" disabled={page <= 1} onClick={() => setPage(page - 1)}>Précédent</button>
          <span className="mini sourdine">page {d.page} / {d.pages}</span>
          <button className="btn btn-s" disabled={page >= d.pages} onClick={() => setPage(page + 1)}>Suivant</button>
        </div>
      )}
    </>
  );
}
